import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrencyAmount } from '../../../theme/tokens';
import { BRAND } from '../../../shared/config/brand';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, CheckIcon, XIcon, CalendarIcon, ClockIcon, CheckCircleIcon, XCircleIcon, EditIcon, ArchiveIcon, UserIcon, CreditCardIcon, LandmarkIcon, WalletIcon, FileTextIcon, ShieldCheckIcon, AlertCircleIcon, DownloadIcon, SmartphoneIcon, PaperclipIcon } from 'lucide-react';
import { mockBills, approveBill, rejectBill, updateBill } from '../mocks/mockBills';
import { mockSuppliers } from '../../suppliers/mocks/mockSuppliers';
export function BillDetail() {
  const {
    t
  } = useTranslation('bills');
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [, forceUpdate] = useState(0);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectNotes, setRejectNotes] = useState('');
  const bill = mockBills.find(b => b.id === id) || mockBills[0];
  const currentUser = 'Simon Verraest';
  // Look up supplier for payout details
  const supplier = mockSuppliers.find(s => s.id === bill.supplierId);
  // Derived state logic
  const isScheduled = bill.status === 'approved' && bill.approvalStatus === 'approved';
  const isDue = bill.status === 'approved' && !isScheduled;
  const isPending = bill.status === 'pending_approval';
  const isPaid = bill.status === 'paid';
  const isRejected = bill.status === 'rejected';
  const isOverdue = bill.status === 'overdue';
  const isAssignedToMe = bill.approver === currentUser;
  const displayStatus = isPaid ? 'Paid' : isRejected ? 'Rejected' : isScheduled ? 'Scheduled' : isPending ? 'Pending Approval' : isOverdue ? 'Overdue' : isDue ? 'Due' : 'Draft';
  const getStatusBadgeClass = () => {
    if (isPaid) return 'bg-green-50 text-green-700 border-green-200';
    if (isRejected || isOverdue) return 'bg-red-50 text-red-700 border-red-200';
    if (isScheduled) return 'bg-blue-50 text-blue-700 border-blue-200';
    if (isPending) return 'bg-yellow-50 text-yellow-700 border-yellow-200';
    return 'bg-gray-100 text-gray-700 border-gray-200';
  };
  // Handlers
  const handleApprove = () => {
    approveBill(bill.id, currentUser);
    forceUpdate(n => n + 1);
  };
  const handleReject = () => {
    rejectBill(bill.id, currentUser, rejectNotes);
    setShowRejectModal(false);
    setRejectNotes('');
    forceUpdate(n => n + 1);
  };
  const handleArchive = () => {
    updateBill(bill.id, {
      status: 'rejected',
      approvalNotes: 'Archived by user'
    });
    forceUpdate(n => n + 1);
  };
  const handleCancelSchedule = () => {
    updateBill(bill.id, {
      status: 'approved',
      approvalStatus: 'pending',
      approver: undefined
    });
    forceUpdate(n => n + 1);
  };
  const formatCurrency = (amount: number) => {
    return formatCurrencyAmount(amount, {
      currency: bill.currency,
      fractionDigits: 2
    });
  };
  const formatDate = (dateString?: string) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };
  // Helper: get payout method display details from supplier
  const getPayoutDetails = () => {
    if (!supplier) return null;
    if (supplier.payoutMethod === 'bank_account') {
      return {
        type: 'Bank Account',
        icon: <LandmarkIcon size={16} className="text-gray-400" data-id="element-3685" />,
        details: [{
          label: 'Bank',
          value: supplier.bankName || '—'
        }, {
          label: 'Account',
          value: supplier.accountNumber ? '•••' + supplier.accountNumber.slice(-4) : '—'
        }, {
          label: 'Account Name',
          value: supplier.accountName || '—'
        }]
      };
    }
    return {
      type: 'PromptPay',
      icon: <SmartphoneIcon size={16} className="text-gray-400" data-id="element-3686" />,
      details: [{
        label: 'Type',
        value: supplier.promptPayType === 'phone' ? 'Phone Number' : supplier.promptPayType === 'citizen_id' ? 'Citizen ID' : 'Tax ID'
      }, {
        label: 'ID',
        value: supplier.promptPayId ? supplier.promptPayId.slice(0, 3) + '••••' + supplier.promptPayId.slice(-3) : '—'
      }, {
        label: 'Name',
        value: supplier.promptPayName || '—'
      }]
    };
  };
  // Helper: get payment source display
  const getPaymentSourceLabel = () => {
    switch (bill.paymentSource) {
      case 'balance':
        return BRAND.balanceLabel;
      case 'card':
        return 'Corporate Card';
      case 'bank_transfer':
        return 'Bank Transfer';
      default:
        return 'Unknown';
    }
  };
  const getPaymentSourceIcon = () => {
    switch (bill.paymentSource) {
      case 'balance':
        return <WalletIcon size={16} className="text-gray-400" data-id="element-3687" />;
      case 'card':
        return <CreditCardIcon size={16} className="text-gray-400" data-id="element-3688" />;
      default:
        return <LandmarkIcon size={16} className="text-gray-400" data-id="element-3689" />;
    }
  };
  // ===== TIMELINE LOGIC (corrected) =====
  // Step statuses: 'completed' | 'current' | 'future' | 'rejected'
  const approvalCompleted = bill.approvalStatus === 'approved' || isScheduled || isPaid || isDue; // isDue means status='approved', so approval happened
  const approvalRejected = bill.approvalStatus === 'rejected' || isRejected;
  const steps = [{
    id: 'created',
    label: 'Bill Created',
    date: bill.issueDate,
    actor: bill.createdBy || currentUser,
    status: 'completed' as const
  }, {
    id: 'approval',
    label: 'Approval',
    date: approvalCompleted || approvalRejected ? bill.approvalDate : undefined,
    actor: approvalCompleted ? bill.approver || 'Auto-approved' : approvalRejected ? bill.approver || 'System' : isPending ? bill.approver || 'Awaiting assignment' : '—',
    status: approvalCompleted ? 'completed' as const : approvalRejected ? 'rejected' as const : isPending ? 'current' as const : 'future' as const
  }, {
    id: 'scheduled',
    label: 'Scheduled',
    date: isScheduled || isPaid ? bill.dueDate : undefined,
    actor: isScheduled || isPaid ? 'System' : undefined,
    status: isScheduled || isPaid ? 'completed' as const : 'future' as const
  }, {
    id: 'payment',
    label: 'Payment',
    date: isPaid ? bill.paidDate : undefined,
    actor: isPaid ? getPaymentSourceLabel() : undefined,
    status: isPaid ? 'completed' as const : isScheduled ? 'current' as const : 'future' as const
  }];
  const payoutDetails = getPayoutDetails();
  return <div className="max-w-7xl mx-auto space-y-6 pb-12" data-id="element-3690">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-3691">
        <button onClick={() => navigate('/bills')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium text-sm transition-colors" data-id="element-3692">
          <ArrowLeftIcon size={18} data-id="element-3693" />
          {t('pages.BillApprovalWizard.back_to_bills')}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start" data-id="element-3694">
        {/* LEFT COLUMN */}
        <div className="lg:col-span-2 space-y-6" data-id="element-3695">
          {/* Card 1: Header & Total */}
          <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm" data-id="element-3696">
            <div className="flex justify-between items-start mb-6" data-id="element-3697">
              <div data-id="element-3698">
                <h1 className="text-3xl font-bold text-gray-900 tracking-tight mb-2" data-id="element-3699">
                  {bill.billNumber}
                </h1>
                <p className="text-gray-500" data-id="element-3700">
                  {bill.description || 'No description provided'}
                </p>
              </div>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${getStatusBadgeClass()}`} data-id="element-3701">
                {displayStatus}
              </span>
            </div>
            <div className="pt-6 border-t border-gray-100" data-id="element-3702">
              <div className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-3703">
                {t('pages.BillDetail.total_amount')}
              </div>
              <div className="text-4xl font-bold text-gray-900" data-id="element-3704">
                {formatCurrency(bill.total)}
              </div>
            </div>
          </div>

          {/* Card: Dates */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm" data-id="element-3705">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-id="element-3706">
              <div data-id="element-3707">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-3708">
                  {t('pages.BillDetail.issue_date')}
                </div>
                <div className="text-sm font-medium text-gray-900 flex items-center gap-2" data-id="element-3709">
                  <CalendarIcon size={14} className="text-gray-400" data-id="element-3710" />
                  {formatDate(bill.issueDate)}
                </div>
              </div>
              <div data-id="element-3711">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-3712">
                  {t('pages.BillDetail.due_date')}
                </div>
                <div className="text-sm font-medium text-gray-900 flex items-center gap-2" data-id="element-3713">
                  <CalendarIcon size={14} className="text-gray-400" data-id="element-3714" />
                  {formatDate(bill.dueDate)}
                </div>
              </div>
              {bill.approvalDate && approvalCompleted && <div data-id="element-3715">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-3716">
                    {t('pages.BillDetail.approval_date')}
                  </div>
                  <div className="text-sm font-medium text-gray-900 flex items-center gap-2" data-id="element-3717">
                    <CheckCircleIcon size={14} className="text-gray-400" data-id="element-3718" />
                    {formatDate(bill.approvalDate)}
                  </div>
                </div>}
              {bill.paidDate && <div data-id="element-3719">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-3720">
                    {t('pages.BillDetail.paid_date')}
                  </div>
                  <div className="text-sm font-medium text-gray-900 flex items-center gap-2" data-id="element-3721">
                    <CheckCircleIcon size={14} className="text-green-500" data-id="element-3722" />
                    {formatDate(bill.paidDate)}
                  </div>
                </div>}
            </div>
          </div>
        </div>

        {/* Card: Supplier */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm" data-id="element-3723">
          <div className="flex justify-between items-center mb-6" data-id="element-3724">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider flex items-center gap-2" data-id="element-3725">
              <UserIcon size={16} className="text-gray-400" data-id="element-3726" />
              {t('pages.BillDetail.supplier_details')}
            </h3>
            <button onClick={() => navigate(`/supplier-detail/${bill.supplierId}`)} className="text-sm font-medium text-[var(--brand-primary)] hover:text-[var(--brand-primaryhover)] transition-colors" data-id="element-3727">
              {t('pages.BillDetail.view_profile')}
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3728">
            <div data-id="element-3729">
              <div className="text-xs text-gray-500 mb-1" data-id="element-3730">
                {t('pages.BillDetail.company_name')}
              </div>
              <div className="text-sm font-medium text-gray-900" data-id="element-3731">
                {bill.supplierName}
              </div>
            </div>
            <div data-id="element-3732">
              <div className="text-xs text-gray-500 mb-1" data-id="element-3733">
                {t('pages.BillDetail.contact_email')}
              </div>
              <div className="text-sm font-medium text-gray-900" data-id="element-3734">
                {bill.supplierEmail}
              </div>
            </div>
            {bill.supplierPhone && <div data-id="element-3735">
                <div className="text-xs text-gray-500 mb-1" data-id="element-3736">
                  {t('pages.BillDetail.phone_number')}
                </div>
                <div className="text-sm font-medium text-gray-900" data-id="element-3737">
                  {bill.supplierPhone}
                </div>
              </div>}
          </div>
        </div>

        {/* Card: Payment Information (Paid/Scheduled) */}
        {(isPaid || isScheduled) && <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm" data-id="element-3738">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider flex items-center gap-2 mb-6" data-id="element-3739">
              <CreditCardIcon size={16} className="text-gray-400" data-id="element-3740" />
              {t('pages.BillDetail.payment_information')}
            </h3>

            {isPaid ? <div className="space-y-6" data-id="element-3741">
                {/* Payment Source */}
                <div data-id="element-3742">
                  <div className="text-xs text-gray-500 uppercase tracking-wider font-medium mb-3" data-id="element-3743">
                    {t('pages.BillDetail.funded_from')}
                  </div>
                  <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-4 border border-gray-100" data-id="element-3744">
                    <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center" data-id="element-3745">
                      {getPaymentSourceIcon()}
                    </div>
                    <div data-id="element-3746">
                      <div className="text-sm font-semibold text-gray-900" data-id="element-3747">
                        {getPaymentSourceLabel()}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-3748">
                        {t('pages.BillDetail.paid_on')}{' '}
                        {formatDate(bill.paidDate)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payout Destination */}
                {payoutDetails && <div data-id="element-3749">
                    <div className="text-xs text-gray-500 uppercase tracking-wider font-medium mb-3" data-id="element-3750">
                      {t('pages.BillDetail.paid_to')}
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-100" data-id="element-3751">
                      <div className="flex items-center gap-3 mb-4" data-id="element-3752">
                        <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center" data-id="element-3753">
                          {payoutDetails.icon}
                        </div>
                        <div className="text-sm font-semibold text-gray-900" data-id="element-3754">
                          {payoutDetails.type}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 pt-3 border-t border-gray-200" data-id="element-3755">
                        {payoutDetails.details.map(d => <div key={d.label} data-id="element-3756">
                            <div className="text-xs text-gray-500 mb-0.5" data-id="element-3757">
                              {d.label}
                            </div>
                            <div className="text-sm font-medium text-gray-900" data-id="element-3758">
                              {d.value}
                            </div>
                          </div>)}
                      </div>
                    </div>
                  </div>}
              </div> : <div className="bg-blue-50 text-blue-800 p-4 rounded-lg flex items-start gap-3 border border-blue-100" data-id="element-3759">
                <ClockIcon size={18} className="text-blue-600 mt-0.5" data-id="element-3760" />
                <div data-id="element-3761">
                  <div className="font-medium text-sm" data-id="element-3762">
                    {t('pages.BillDetail.scheduled_for_payment')}
                  </div>
                  <div className="text-xs mt-1 text-blue-700" data-id="element-3763">
                    {t('pages.BillDetail.this_bill_is_scheduled_to_be')}{' '}
                    {formatDate(bill.dueDate)}.
                  </div>
                </div>
              </div>}
          </div>}

        {/* Card: Attachment (replaces Line Items) */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm" data-id="element-3764">
          <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider flex items-center gap-2 mb-6" data-id="element-3765">
            <PaperclipIcon size={16} className="text-gray-400" data-id="element-3766" />
            {t('pages.BillDetail.attachment')}
          </h3>

          {bill.attachmentFileName ? <div className="flex items-center justify-between bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-3767">
              <div className="flex items-center gap-3 min-w-0" data-id="element-3768">
                <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0" data-id="element-3769">
                  <FileTextIcon size={20} className="text-red-500" data-id="element-3770" />
                </div>
                <div className="min-w-0" data-id="element-3771">
                  <div className="text-sm font-medium text-gray-900 truncate" data-id="element-3772">
                    {bill.attachmentFileName}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-3773">
                    {t('pages.BillDetail.uploaded_with_bill')}
                  </div>
                </div>
              </div>
              <button className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-[var(--brand-primary)] hover:bg-[var(--brand-primarysubtle)] rounded-lg transition-colors flex-shrink-0" data-id="element-3774">
                <DownloadIcon size={16} aria-hidden="true" data-id="element-3775" />
                {t('pages.BillDetail.download')}
              </button>
            </div> : <div className="text-center py-8 text-gray-400" data-id="element-3776">
              <PaperclipIcon size={24} className="mx-auto mb-2 opacity-50" data-id="element-3777" />
              <p className="text-sm" data-id="element-3778">
                {t('pages.BillDetail.no_file_attached_to_this_bill')}
              </p>
            </div>}

          {/* Amount Summary */}
          <div className="mt-6 pt-6 border-t border-gray-200 flex justify-end" data-id="element-3779">
            <div className="w-full max-w-xs space-y-3" data-id="element-3780">
              <div className="flex justify-between text-sm" data-id="element-3781">
                <span className="text-gray-500" data-id="element-3782">
                  {t('pages.BillDetail.subtotal')}
                </span>
                <span className="text-gray-900 font-medium" data-id="element-3783">
                  {formatCurrency(bill.subtotal)}
                </span>
              </div>
              {bill.taxAmount && bill.taxAmount > 0 && <div className="flex justify-between text-sm" data-id="element-3784">
                  <span className="text-gray-500" data-id="element-3785">
                    {t('pages.BillDetail.tax')}
                  </span>
                  <span className="text-gray-900 font-medium" data-id="element-3786">
                    {formatCurrency(bill.taxAmount)}
                  </span>
                </div>}
              {bill.discountAmount && bill.discountAmount > 0 && <div className="flex justify-between text-sm" data-id="element-3787">
                  <span className="text-gray-500" data-id="element-3788">
                    {t('pages.BillDetail.discount')}
                  </span>
                  <span className="text-red-600 font-medium" data-id="element-3789">
                    -{formatCurrency(bill.discountAmount)}
                  </span>
                </div>}
              <div className="flex justify-between pt-3 border-t border-gray-200" data-id="element-3790">
                <span className="text-base font-bold text-gray-900" data-id="element-3791">
                  {t('pages.BillDetail.total')}
                </span>
                <span className="text-base font-bold text-gray-900" data-id="element-3792">
                  {formatCurrency(bill.total)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Card: Notes */}
        {bill.notes && <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm" data-id="element-3793">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4" data-id="element-3794">
              {t('pages.BillDetail.notes')}
            </h3>
            <div className="bg-gray-50 p-4 rounded-lg text-sm text-gray-700 border border-gray-100" data-id="element-3795">
              {bill.notes}
            </div>
          </div>}
      </div>

      {/* RIGHT COLUMN */}
      <div className="lg:col-span-1 space-y-6" data-id="element-3796">
        {/* ACTIONS CARD */}
        <div className={`bg-white rounded-xl border shadow-sm overflow-hidden ${isPending && isAssignedToMe ? 'border-l-4 border-l-[var(--brand-primary)] border-y-gray-200 border-r-gray-200' : 'border-gray-200'}`} data-id="element-3797">
          <div className="p-6" data-id="element-3798">
            {/* DUE */}
            {isDue && <>
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-5" data-id="element-3799">
                  {t('pages.BillDetail.actions')}
                </h3>
                <div className="space-y-3" data-id="element-3800">
                  <button onClick={() => navigate(`/bill-approval/schedule?ids=${bill.id}`)} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[var(--brand-primary)] text-[#1A1A1A] rounded-lg hover:bg-[var(--brand-primaryhover)] text-sm font-medium shadow-sm transition-colors" data-id="element-3801">
                    <CalendarIcon size={16} data-id="element-3802" />
                    {t('pages.BillDetail.schedule_payment')}
                  </button>
                  <button onClick={handleArchive} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium transition-colors" data-id="element-3803">
                    <ArchiveIcon size={16} data-id="element-3804" />
                    {t('pages.BillDetail.archive_bill')}
                  </button>
                  <button onClick={() => navigate(`/bill-form?id=${bill.id}`)} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-[var(--brand-primary)] hover:bg-[var(--brand-primarysubtle)] rounded-lg text-sm font-medium transition-colors mt-2" data-id="element-3805">
                    <EditIcon size={16} aria-hidden="true" data-id="element-3806" />
                    {t('pages.BillDetail.edit_details')}
                  </button>
                </div>
              </>}

            {/* PENDING */}
            {isPending && <>
                {isAssignedToMe ? <>
                    <div className="flex items-center gap-2 mb-3" data-id="element-3807">
                      <ShieldCheckIcon size={18} className="text-[var(--brand-primary)]" data-id="element-3808" />
                      <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider" data-id="element-3809">
                        {t('pages.BillDetail.approval_required')}
                      </h3>
                    </div>
                    <p className="text-sm text-gray-600 mb-6" data-id="element-3810">
                      {t('pages.BillDetail.this_bill_has_been_assigned_to')}
                    </p>
                    <div className="space-y-3" data-id="element-3811">
                      <button onClick={handleApprove} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[var(--brand-primary)] text-[#1A1A1A] rounded-lg hover:bg-[var(--brand-primaryhover)] text-sm font-medium shadow-sm transition-colors" data-id="element-3812">
                        <CheckIcon size={16} data-id="element-3813" />
                        {t('pages.BillDetail.approve_bill')}
                      </button>
                      <button onClick={() => setShowRejectModal(true)} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white text-red-600 border border-red-200 rounded-lg hover:bg-red-50 text-sm font-medium transition-colors" data-id="element-3814">
                        <XIcon size={16} data-id="element-3815" />
                        {t('pages.BillDetail.reject_bill')}
                      </button>
                    </div>
                  </> : <>
                    <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4" data-id="element-3816">
                      {t('pages.BillDetail.status')}
                    </h3>
                    <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-4 flex items-start gap-3" data-id="element-3817">
                      <ClockIcon size={18} className="text-yellow-600 mt-0.5" data-id="element-3818" />

                      <div data-id="element-3819">
                        <div className="text-sm font-medium text-yellow-900" data-id="element-3820">
                          {t('pages.BillDetail.awaiting_approval')}
                        </div>
                        <div className="text-xs text-yellow-700 mt-1" data-id="element-3821">
                          {t('pages.BillDetail.assigned_to')}{' '}
                          <span className="font-semibold" data-id="element-3822">
                            {bill.approver || 'Unassigned'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </>}
              </>}

            {/* SCHEDULED */}
            {isScheduled && <>
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4" data-id="element-3823">
                  {t('pages.BillDetail.status')}
                </h3>
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 flex items-start gap-3 mb-6" data-id="element-3824">
                  <CalendarIcon size={18} className="text-blue-600 mt-0.5" data-id="element-3825" />
                  <div data-id="element-3826">
                    <div className="text-sm font-medium text-blue-900" data-id="element-3827">
                      {t('pages.BillDetail.payment_scheduled')}
                    </div>
                    <div className="text-xs text-blue-700 mt-1" data-id="element-3828">
                      {t('pages.BillDetail.will_be_paid_on')}{' '}
                      {formatDate(bill.dueDate)}
                    </div>
                  </div>
                </div>
                <button onClick={handleCancelSchedule} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium transition-colors" data-id="element-3829">
                  <XIcon size={16} aria-hidden="true" data-id="element-3830" />
                  {t('pages.BillDetail.cancel_schedule')}
                </button>
              </>}

            {/* PAID */}
            {isPaid && <>
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4" data-id="element-3831">
                  {t('pages.BillDetail.status')}
                </h3>
                <div className="bg-green-50 border border-green-100 rounded-lg p-4 flex items-start gap-3" data-id="element-3832">
                  <CheckCircleIcon size={18} className="text-green-600 mt-0.5" data-id="element-3833" />

                  <div data-id="element-3834">
                    <div className="text-sm font-medium text-green-900" data-id="element-3835">
                      {t('pages.BillDetail.payment_complete')}
                    </div>
                    <div className="text-xs text-green-700 mt-1" data-id="element-3836">
                      {t('pages.BillDetail.successfully_paid_on')}{' '}
                      {formatDate(bill.paidDate)}
                    </div>
                  </div>
                </div>
              </>}

            {/* REJECTED */}
            {isRejected && <>
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4" data-id="element-3837">
                  {t('pages.BillDetail.status')}
                </h3>
                <div className="bg-red-50 border border-red-100 rounded-lg p-4 flex items-start gap-3" data-id="element-3838">
                  <XCircleIcon size={18} className="text-red-600 mt-0.5" data-id="element-3839" />
                  <div data-id="element-3840">
                    <div className="text-sm font-medium text-red-900" data-id="element-3841">
                      {t('pages.BillDetail.bill_rejected')}
                    </div>
                    <div className="text-xs text-red-700 mt-1" data-id="element-3842">
                      {t('pages.BillDetail.rejected_by')}{' '}
                      {bill.approver || 'System'}
                    </div>
                  </div>
                </div>
                {bill.approvalNotes && <div className="mt-4 text-sm text-gray-600 bg-gray-50 p-3 rounded border border-gray-100" data-id="element-3843">
                    <span className="font-medium text-gray-900 block mb-1" data-id="element-3844">
                      {t('pages.BillDetail.reason')}
                    </span>
                    {bill.approvalNotes}
                  </div>}
              </>}
          </div>
        </div>

        {/* TIMELINE CARD */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm" data-id="element-3845">
          <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-6" data-id="element-3846">
            {t('pages.BillDetail.workflow_timeline')}
          </h3>

          <div className="space-y-0 relative" data-id="element-3847">
            {steps.map((step, index) => {
            const nextStep = steps[index + 1];
            // Line is dark only if current step is completed AND next step is also completed or current
            const lineDark = step.status === 'completed' && nextStep && (nextStep.status === 'completed' || nextStep.status === 'current');
            return <div key={step.id} className="flex gap-4 relative" data-id="element-3848">
                  {/* Connector Line */}
                  {index < steps.length - 1 && <div className={`absolute left-[11px] top-7 bottom-[-7px] w-0.5 ${lineDark ? 'bg-gray-900' : 'bg-gray-200'}`} data-id="element-3849" />}

                  {/* Indicator */}
                  <div className="relative z-10 flex flex-col items-center mt-1" data-id="element-3850">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${step.status === 'completed' ? 'bg-gray-900 text-white' : step.status === 'current' ? 'bg-white border-2 border-[var(--brand-primary)]' : step.status === 'rejected' ? 'bg-red-100 text-red-600' : 'bg-gray-100 border border-gray-200'}`} data-id="element-3851">
                      {step.status === 'completed' && <CheckIcon size={12} strokeWidth={3} data-id="element-3852" />}
                      {step.status === 'current' && <div className="w-2 h-2 rounded-full bg-[var(--brand-primary)]" data-id="element-3853" />}
                      {step.status === 'rejected' && <XIcon size={12} strokeWidth={3} data-id="element-3854" />}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="pb-8 min-w-0 flex-1" data-id="element-3855">
                    <div className="flex items-center justify-between" data-id="element-3856">
                      <span className={`text-sm font-medium ${step.status === 'future' ? 'text-gray-400' : step.status === 'rejected' ? 'text-red-700' : 'text-gray-900'}`} data-id="element-3857">
                        {step.label}
                      </span>
                      {step.date && step.status !== 'future' && <span className="text-xs text-gray-500" data-id="element-3858">
                          {formatDate(step.date)}
                        </span>}
                    </div>
                    {step.status === 'current' ? <div className="text-xs mt-1 text-[var(--brand-primary)] font-medium flex items-center gap-1" data-id="element-3859">
                        <ClockIcon size={12} data-id="element-3860" /> {t('pages.BillDetail.awaiting')}
                      </div> : step.actor && step.status !== 'future' ? <div className="text-xs mt-1 text-gray-500" data-id="element-3861">
                        {step.actor}
                      </div> : null}
                  </div>
                </div>;
          })}
          </div>
        </div>
      </div>
    </div> /* Reject Modal */;
  {
    /* Reject Modal */
  }
  {
    showRejectModal && <>
        <div className="fixed inset-0 bg-black/50 z-[60]" onClick={() => setShowRejectModal(false)} data-id="element-3862" />

        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-id="element-3863">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden" data-id="element-3864">
            <div className="p-8" data-id="element-3865">
              <div className="flex items-center gap-3 mb-4" data-id="element-3866">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center" data-id="element-3867">
                  <AlertCircleIcon size={20} className="text-red-600" data-id="element-3868" />
                </div>
                <h2 className="text-lg font-bold text-gray-900" data-id="element-3869">
                  {t('pages.BillDetail.reject_bill')}
                </h2>
              </div>
              <p className="text-sm text-gray-600 mb-6" data-id="element-3870">
                {t('pages.BillDetail.are_you_sure_you_want_to')}{' '}
                <span className="font-medium text-gray-900" data-id="element-3871">
                  {bill.billNumber}
                </span>
                {t('pages.BillDetail.this_action_will_notify_the_submitter')}
              </p>

              <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-3872">
                {t('pages.BillDetail.reason_for_rejection')}
              </label>
              <textarea value={rejectNotes} onChange={e => setRejectNotes(e.target.value)} rows={3} placeholder={t('pages.BillDetail.attrs.please_provide_a_reason')} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500/20 focus:border-red-400 transition-colors resize-none" data-id="element-3873" />

              <div className="flex justify-end gap-3 mt-8" data-id="element-3874">
                <button onClick={() => {
                setShowRejectModal(false);
                setRejectNotes('');
              }} className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-3875">
                  {t('pages.BillApprovalWizard.cancel')}
                </button>
                <button onClick={handleReject} disabled={!rejectNotes.trim()} className={`px-5 py-2.5 text-sm font-medium text-white rounded-lg transition-colors ${rejectNotes.trim() ? 'bg-red-600 hover:bg-red-700 shadow-sm' : 'bg-red-300 cursor-not-allowed'}`} data-id="element-3876">
                  {t('pages.BillDetail.reject_bill')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </>;
  }
}